import React, { useState } from "react";
import moment from "moment";
import { Button } from "./ScheduleEditorUI";
import { DAYS_OF_WEEK } from "./constants";
import { collapseWindowsToShifts } from "./time";

const dateLabel = (date) => moment.utc(date, "YYYY-MM-DD").format("MMM D, YYYY");
const timeLabel = (time) => moment.utc(time, "HH:mm").format("h:mm A");
const hoursLabel = (shift) => `${timeLabel(shift.startTime)} – ${timeLabel(shift.endTime)}${shift.startDayOffset ? " (following day)" : shift.endTime <= shift.startTime ? " (next day)" : ""}`;

function SummaryCard({ rule, categories, onEdit, onDeactivate }) {
  const [selectedWeek, setSelectedWeek] = useState(0);
  const cycleWeeks = Number(rule.cycleWeeks) || 1;
  const week = Math.min(selectedWeek, cycleWeeks - 1);
  const shifts = collapseWindowsToShifts(rule.weeklyWindows || []);
  const weekShifts = shifts.filter((shift) => Number(shift.weekIndex || 0) === week);
  return <article className="work-summary-card" aria-label={`Work schedule from ${dateLabel(rule.effectiveFrom)}`}>
    <header className="work-summary-header">
      <div>
        <h4>{rule.effectiveTo ? `${dateLabel(rule.effectiveFrom)} – ${dateLabel(rule.effectiveTo)}` : `From ${dateLabel(rule.effectiveFrom)} · No end date`}</h4>
        <p>{cycleWeeks === 1 ? "Repeats every week" : `Repeats every ${cycleWeeks} weeks`}
          {cycleWeeks > 1 && rule.cycleAnchorDate ? ` · Week 1 starts ${dateLabel(rule.cycleAnchorDate)}` : ""}</p>
      </div>
      <Button variant="secondary" onClick={() => onEdit(rule)}>Edit hours</Button>
    </header>
    {cycleWeeks > 1 && <div className="schedule-segments work-summary-weeks" aria-label="Rotation week">
      {Array.from({ length: cycleWeeks }, (_, index) => <button key={index} type="button" aria-pressed={week === index}
        onClick={() => setSelectedWeek(index)}>Week {index + 1}</button>)}
    </div>}
    <div className="work-summary-days">
      {DAYS_OF_WEEK.map(({ value, name }) => {
        const dayShifts = weekShifts.filter((shift) => shift.dayOfWeek === value);
        return <section key={value} className={`work-summary-day ${dayShifts.length ? "is-working" : "is-off"}`} aria-label={name}>
          <h5>{name}</h5>
          {!dayShifts.length ? <p className="work-summary-off">Day off</p> : dayShifts.map((shift, index) =>
            <div className="work-summary-shift" key={index}>
              <p className="work-summary-time">{hoursLabel(shift)}</p>
              {!!shift.breaks?.length && <div className="work-summary-break">
                <span className="work-summary-break-label">{shift.breaks.length > 1 ? "Breaks" : "Break"}</span>
                <ul className="work-summary-break-list" aria-label={`${name} shift ${index + 1} breaks`}>
                  {shift.breaks.map((pause, pauseIndex) => <li key={pauseIndex}>{hoursLabel(pause)}</li>)}
                </ul>
              </div>}
              {!!shift.serviceCategoryIds?.length && <p className="work-summary-types">{shift.serviceCategoryIds.map((id) =>
                categories.find((category) => category.serviceCategoryId === id)?.name || "Appointment type unavailable").join(", ")}</p>}
            </div>)}
        </section>;
      })}
    </div>
    <footer className="work-summary-footer">
      <span>Date changes and blocked time are managed separately.</span>
      <details className="work-summary-actions"><summary>More actions</summary>
        <button type="button" onClick={() => onDeactivate(rule)}>Deactivate work schedule</button>
      </details>
    </footer>
  </article>;
}

export default function WorkScheduleSummary({ rules = [], categories = [], isLoading, isError, onRetry, onEdit, onDeactivate }) {
  if (isLoading) return <p role="status" className="scheduling-empty">Loading working hours…</p>;
  if (isError) return <div className="scheduling-empty" role="alert">Working hours could not be loaded.
    <button type="button" className="work-summary-retry" onClick={onRetry}>Try again</button>
  </div>;
  const activeRules = rules.filter((rule) => rule.active);
  if (!activeRules.length) return <div className="work-summary-empty">
    <h4>No regular hours yet</h4><p>Add a work schedule to choose your working days, hours and breaks.</p>
  </div>;
  return <div className="work-summary-list">{activeRules.map((rule) => <SummaryCard key={rule.availabilityRuleId}
    rule={rule} categories={categories} onEdit={onEdit} onDeactivate={onDeactivate} />)}</div>;
}
